import React, { useRef, useState } from "react";
import { useSwipeable } from "react-swipeable";
import { InboundItem, MediaItem } from "../../../types/item";
import { FaCamera, FaTrash } from "react-icons/fa";
import { ItemStatus } from "../../../types/enums/item";

interface ItemCardProps {
  item: InboundItem;
  onDelete?: (item: InboundItem) => void;
  onEdit?: (item: InboundItem) => void;
  maxSwipeDistance?: number;
  disableSwipe?: boolean;
}

const ItemCard: React.FC<ItemCardProps> = ({
  item,
  onDelete,
  onEdit,
  maxSwipeDistance = 80,
  disableSwipe = false,
}) => {
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSwipeActive, setIsSwipeActive] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [startTime, setStartTime] = useState<number | null>(null);

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(item);
    }
  };

  const handleEdit = () => {
    if (!isSwipeActive && swipeOffset === 0 && onEdit) {
      onEdit(item);
    }
  };

  const swipeHandlers = useSwipeable({
    onSwiping: disableSwipe
      ? undefined
      : (eventData) => {
          // Only trigger swipe on horizontal movement
          if (Math.abs(eventData.deltaX) > Math.abs(eventData.deltaY)) {
            setIsSwipeActive(true);
            const maxSwipe = maxSwipeDistance;
            const limitedDeltaX = Math.max(
              -maxSwipe,
              Math.min(0, eventData.deltaX)
            );
            setSwipeOffset(limitedDeltaX);
          }
        },
    onSwipedLeft: disableSwipe
      ? undefined
      : (eventData) => {
          if (Math.abs(eventData.deltaX) > 50) {
            // Swipe left to reveal delete action
            setSwipeOffset(-maxSwipeDistance);
          } else {
            setSwipeOffset(0);
          }
          // Keep isSwipeActive true for a short time to prevent click
          setTimeout(() => setIsSwipeActive(false), 200);
        },
    onSwipedRight: disableSwipe
      ? undefined
      : () => {
          // Disable right swipe - always reset to 0
          setSwipeOffset(0);
          // Keep isSwipeActive true for a short time to prevent click
          setTimeout(() => setIsSwipeActive(false), 200);
        },
    trackMouse: !disableSwipe,
    preventScrollOnSwipe: false, // Allow vertical scrolling
    delta: 10, // Require 10px movement before triggering swipe
  });

  // const handleMouseUp = () => {
  //   handleEdit();
  // };

  const handleTouchStart = () => {
    setStartTime(Date.now());

    timeoutRef.current = setTimeout(() => {
      console.log("Long press - show delete option");
    }, 800);
  };

  const handleTouchEnd = () => {
    if (startTime) {
      const duration = Date.now() - startTime;

      if (duration < 200) {
        // handleEdit();
      } else if (duration < 800) {
        // Medium press
      }
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setStartTime(null);
  };

  const renderStatus = (status_ID: ItemStatus) => {
    if (status_ID === ItemStatus.ErrorProcessing) {
      return (
        <span className="rounded-full bg-red-500 text-white text-xs px-2 py-[2px] ml-1">
          Error
        </span>
      );
    }
    if (status_ID === ItemStatus.Pending) {
      return (
        <span className="rounded-full bg-yellow-500 text-white text-xs px-2 py-[2px] ml-1">
          pending
        </span>
      );
    } else {
      return null;
    }
  };

  return (
    <div className="relative rounded-xl shadow-md cursor-pointer transition-all duration-300 overflow-hidden border-gray-200 border">
      <div className="absolute inset-0 flex justify-end rounded-xl border-gray-200 border">
        <div
          className="bg-red-500 flex items-center justify-center rounded-r-xl"
          style={{ width: "80px" }}
        >
          <button
            onClick={handleDelete}
            className="text-white font-medium flex-col items-center justify-center"
          >
            <FaTrash size={18} className="mx-auto" />
            <div className="text-xs mt-1">Delete</div>
          </button>
        </div>
      </div>

      <div
        {...swipeHandlers}
        className={`ios-text-selection-prevention relative p-4 shadow-card-shadow w-full bg-white cursor-pointer transition-all duration-200 ease-out ${
          swipeOffset < 0 ? "rounded-l-xl rounded-r-none" : "rounded-xl"
        } `}
        style={{
          transform: `translateX(${swipeOffset}px)`,
          userSelect: "none",
          WebkitUserSelect: "none",
          MozUserSelect: "none",
          msUserSelect: "none",
          WebkitTouchCallout: "none",
          WebkitTapHighlightColor: "transparent",
          WebkitTextSizeAdjust: "none",
          touchAction: "pan-y", // Allow vertical scroll, prevent horizontal
        }}
      >
        <div
          className="flex justify-between items-start"
          // onMouseUp={handleMouseUp}
          onClick={handleEdit}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div className="flex-1">
            <h3 className="font-semibold text-lg text-gray-800 mb-0">
              <span className="text-gray-800 mr-1">{item.description}</span>
              {renderStatus(item.status_ID as ItemStatus)}
            </h3>
            {/* <div className="text-xs text-gray-400">
              CONSIGNOR CODE: {item.consignorCode ? item.consignorCode : "-"}
            </div> */}
            <div className="text-xs text-gray-400">Item #: {parseInt(item.id as string) > 0 ? item.id : "-"}</div>
          </div>
          <div></div>
          <div className="ml-4 flex flex-col items-end">
            <div
              className={`bg-primary px-2 py-[2px] rounded-full text-xs font-medium text-white  mb-2 flex items-center gap-2`}
            >
              <FaCamera size={12} />
              {item.media?.filter((media: MediaItem) => !media.toDelete).length || 0}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItemCard;
